/**
 * "We have seen this before" — the agent-facing end of the KB pipeline.
 *
 * IT IS A TAB, NOT A BANNER. The first version put the whole panel above the
 * conversation and it cost half the screen on a page whose job is the
 * thread. What belongs above the fold is one line saying a known answer
 * exists; the detail lives behind a tab, with a glint on the tab icon the
 * first time it appears so nobody has to notice it unaided.
 *
 * THREE THINGS AN AGENT ACTUALLY NEEDS, in this order:
 *   1. the fix that worked, verbatim from the tickets — not a summary of it;
 *   2. whether the requester could already see this article, because if they
 *      could and they filed anyway, the obvious answer is not the answer;
 *   3. how it was found, since a lexical fallback and a vector match deserve
 *      different amounts of trust.
 *
 * And one thing it must not do: look confident when it is guessing. A weak
 * match is not shown at all, and an empty tab says so in a sentence rather
 * than padding itself with four unrelated articles.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ChevronDown, ChevronRight, Lightbulb, Search, Wrench } from 'lucide-react';
import { cleanChunkText, splitArticle } from '@esa/shared';
import { ArticleSectionsView } from './ArticleSectionsEditor.js';
import { api, type TicketSuggestion, type TicketSuggestions } from '../lib/api.js';
import { Badge, Button, Skeleton, Tooltip, cn } from '../ui/index.js';
import { dateTime } from '../lib/format.js';

/**
 * One query, three readers: the tab's count, the one-line hint above the
 * thread, and the panel itself. TanStack dedupes on the key, so this is a
 * single request however many components ask for it.
 */
export function useKnownSolutions(tenant: string, number: string, enabled = true) {
  return useQuery({
    queryKey: ['ticket-suggestions', tenant, number],
    // A requester never asks: the API refuses them, and firing it anyway
    // would put a 403 in their console on every ticket they open.
    enabled: enabled && Boolean(tenant) && Boolean(number),
    queryFn: () =>
      api.get<TicketSuggestions>(`/api/v1/tenants/${tenant}/tickets/${number}/suggestions`),
    // The knowledgebase does not change while an agent reads one ticket, and
    // re-embedding the thread on every window focus costs money.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // Nor on the default 30s poll, which `staleTime` does not hold back: an
    // interval fires on its own schedule regardless of staleness. The line
    // above meant to stop paid re-runs and only ever closed one door.
    refetchInterval: false,
    retry: false,
  });
}

/**
 * The one line that earns its place above the thread: that there is a known
 * answer, and what it is called. Everything else is a click away.
 */
export function KnownSolutionsHint({
  tenant,
  number,
  onOpen,
}: {
  tenant: string;
  number: string;
  onOpen: () => void;
}) {
  const q = useKnownSolutions(tenant, number);
  const top = q.data?.suggestions[0];
  if (!top) return null;

  const more = (q.data?.suggestions.length ?? 1) - 1;

  // NOTHING STRONG MEANS DO NOT CALL IT A SOLUTION. The knowledgebase has no
  // forgotten-password article, so a ticket asking for a reset returns
  // "Change Password Using Okta" at 0.50 — a different task in similar
  // words. Labelling that "Known solution" is how an agent is sent to the
  // wrong answer with the system's confidence behind it.
  const strong = (q.data?.suggestions ?? []).some((s) => s.confidence === 'strong');

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border px-3 py-1.5 text-left transition-colors',
        strong
          ? 'border-brand-line/60 bg-brand-tint/40 hover:bg-brand-tint'
          : 'border-line-subtle bg-surface-sunken hover:bg-surface-hover',
      )}
    >
      <Lightbulb
        className={cn('h-3.5 w-3.5 shrink-0', strong ? 'text-brand' : 'text-ink-tertiary')}
      />
      <span className="min-w-0 flex-1 truncate text-xs text-ink">
        <span className="font-medium">
          {strong ? 'Known solution:' : 'Closest in the knowledgebase:'}
        </span>{' '}
        {top.title}
        {more > 0 && <span className="text-ink-tertiary"> +{more} more</span>}
      </span>
      <span className={cn('shrink-0 text-2xs', strong ? 'text-brand' : 'text-ink-tertiary')}>
        View
      </span>
    </button>
  );
}

/**
 * The tab's own icon, which glints three times when there is something to
 * see. Three, not forever: an indicator that never stops is wallpaper, and
 * this one is competing with the ticket the agent came to read.
 */
export function KnownSolutionsTabIcon({ live }: { live: boolean }) {
  return (
    <span className="relative inline-flex">
      {live && <span aria-hidden className="anim-glint absolute inset-0" />}
      <Lightbulb className={cn('h-3.5 w-3.5', live ? 'text-brand' : 'text-ink-tertiary')} />
    </span>
  );
}

export function KnownSolutions({
  tenant,
  number,
  onUse,
}: {
  tenant: string;
  number: string;
  /** Drop the fix into the reply composer. */
  onUse?: (text: string) => void;
}) {
  const q = useKnownSolutions(tenant, number);

  if (q.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  // A failure here is not the agent's problem to solve. It says so quietly
  // and stays out of the way; the ticket is still workable.
  if (q.error || !q.data) {
    return (
      <p className="text-xs text-ink-tertiary">
        The knowledgebase could not be searched just now.
        {q.error instanceof Error ? ` (${q.error.message})` : ''}
      </p>
    );
  }

  const { suggestions, retrieval, note, query } = q.data;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="max-w-lg text-2xs leading-relaxed text-ink-tertiary">
          {suggestions.length > 0
            ? 'Matched against what the requester wrote, not the whole thread — which would find the assistant’s own auto-answer.'
            : ''}
        </p>
        <RetrievalBadge retrieval={retrieval} query={query} />
      </div>

      {suggestions.length === 0 ? (
        <p className="py-6 text-center text-xs text-ink-tertiary">{note ?? 'Nothing matched.'}</p>
      ) : (
        <>
          {suggestions.map((s) => (
            <SuggestionCard key={s.articleId} tenant={tenant} number={number} s={s} onUse={onUse} />
          ))}
          {/* Under the results, not instead of them: a degraded search can
              still return the right article. */}
          {retrieval === 'lexical' && note && (
            <p className="text-[11px] leading-relaxed text-warn">{note}</p>
          )}
        </>
      )}
    </div>
  );
}

function RetrievalBadge({ retrieval, query }: { retrieval: string; query: string }) {
  if (retrieval === 'none') return null;
  return (
    <Tooltip
      content={
        <span className="block max-w-xs text-left">
          {retrieval === 'vector'
            ? 'Matched on meaning, against this group’s knowledge collections.'
            : 'Matched on wording, against this system’s own published articles only.'}
          <span className="mt-1 block text-ink-tertiary">
            Searched for: “{query.slice(0, 180)}”
          </span>
        </span>
      }
    >
      <Badge tone={retrieval === 'vector' ? 'info' : 'warn'} icon={<Search className="h-3 w-3" />}>
        {retrieval === 'vector' ? 'semantic match' : 'wording match'}
      </Badge>
    </Tooltip>
  );
}

function SuggestionCard({
  tenant,
  number,
  s,
  onUse,
}: {
  tenant: string;
  number: string;
  s: TicketSuggestion;
  onUse?: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // From the server, not recomputed here: one definition of "strong enough
  // to call an answer", shared by the hint, the card and the tests.
  const strong = s.confidence === 'strong';

  /**
   * A ref, not state: a double-click fires two synchronous event handlers
   * before either render commits, so a `useState` guard would still let both
   * through. This card is keyed by `s.articleId`, so the ref is already
   * scoped to one article.
   */
  const recordedUse = useRef(false);

  function handleUse() {
    // The reply must land whether or not the measurement does — this is
    // recording a signal, not part of the write path. Never await it, and
    // never surface its failure: an agent who cannot get their own answer
    // into the box because a metrics call 500'd is a worse outcome than a
    // missing data point.
    onUse?.(replyText(s));

    if (recordedUse.current) return;
    recordedUse.current = true;
    void api
      .post(`/api/v1/tenants/${tenant}/tickets/${number}/suggestions/${s.articleId}/used`)
      .catch(() => {
        /* best-effort measurement; see comment above */
      });
  }

  return (
    <article
      className={cn(
        'rounded-lg border p-3',
        strong ? 'border-brand-line/60 bg-brand-tint/40' : 'border-line-subtle bg-surface-sunken',
      )}
    >
      <header className="flex items-start gap-2">
        <Lightbulb
          className={cn('mt-0.5 h-4 w-4 shrink-0', strong ? 'text-brand' : 'text-ink-tertiary')}
        />
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-medium text-ink">{s.title}</h4>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {/* A percentage, not a bar: it is one number, and the agent is
                deciding whether to read the thing, not comparing four. */}
            <Badge
              tone={strong ? 'brand' : 'warn'}
              title={
                strong
                  ? `retriever score ${s.score}`
                  : `retriever score ${s.score} — below the bar for calling this the answer. ` +
                    'Often the nearest article about a different task.'
              }
            >
              {Math.round(s.score * 100)}% match{strong ? '' : ' — closest, not confirmed'}
            </Badge>
            {s.pattern && (
              <Badge tone="neutral" title={s.pattern.problem}>
                {s.pattern.tickets} ticket{s.pattern.tickets === 1 ? '' : 's'}
              </Badge>
            )}
            {s.lane !== 'unknown' && (
              <Badge tone={s.lane === 'external' ? 'accent' : 'neutral'}>
                {s.lane === 'external' ? 'self-serve' : 'desk only'}
              </Badge>
            )}
            {s.origin === 'knowledgebase' && (
              <Badge tone="neutral" title={`${s.collection}/${s.source}`}>
                existing KB
              </Badge>
            )}
            {s.updatedAt && (
              <span className="text-[11px] text-ink-tertiary">updated {dateTime(s.updatedAt)}</span>
            )}
          </div>
        </div>
      </header>

      {/* The most valuable line on the card. If the answer is already where
          the requester could read it and they filed anyway, the agent needs
          to know that before sending it back to them. */}
      {s.alreadyOffered && (
        <p className="mt-2 rounded border border-warn-line/60 bg-warn-tint px-2 py-1 text-[11px] leading-relaxed text-warn">
          Published where the requester can read it — they filed anyway. Check it actually covers
          their case before sending it back.
        </p>
      )}

      {s.fixes.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-ink-tertiary">
            <Wrench className="h-3 w-3" /> What worked before
          </p>
          {s.fixes.map((f, i) => (
            <div key={i} className="rounded bg-surface px-2 py-1.5 text-xs leading-relaxed">
              <p className="text-ink">{f.fix}</p>
              {f.cause && <p className="mt-0.5 text-ink-tertiary">Cause: {f.cause}</p>}
            </div>
          ))}
        </div>
      )}

      {/* THE WHOLE ARTICLE, laid out as its sections. This used to be the
          retriever's snippet -- one chunk, which for a sectioned article was
          the Symptom alone, so the steps an agent opened it for were not
          there. The snippet is only the fallback now. */}
      {open && (
        <div className="mt-2 rounded bg-surface px-2.5 py-2">
          {s.body?.trim() ? (
            <ArticleSectionsView body={s.body} />
          ) : (
            <p className="whitespace-pre-wrap text-xs leading-relaxed text-ink-secondary">
              {cleanChunkText(s.snippet) || 'The indexed text here is only a metadata header.'}
            </p>
          )}
        </div>
      )}

      <footer className="mt-2 flex flex-wrap items-center gap-1.5">
        <Button size="xs" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {open ? 'Hide article' : 'Read article'}
        </Button>
        {onUse && (
          <Button
            size="xs"
            variant="secondary"
            onClick={handleUse}
            title="Put this in the reply box — then edit it before sending"
          >
            Use in reply
          </Button>
        )}
        {s.origin === 'tributary' && s.pattern && (
          <Link
            to={`/knowledge?group=${tenant}&pattern=${s.pattern.id}`}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-ink-secondary hover:bg-surface-hover hover:text-ink"
          >
            <BookOpen className="h-3 w-3" /> Pattern
          </Link>
        )}
        {s.sourceUrl && (
          <a
            href={s.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-brand hover:underline"
          >
            Open in knowledgebase
          </a>
        )}
      </footer>
    </article>
  );
}

/**
 * What lands in the reply box.
 *
 * WRITTEN FOR THE REQUESTER, not assembled from the record. The first
 * version pasted the retrieved chunk verbatim, so an agent who pressed Use
 * in reply sent somebody a line reading `[Path: Symptom > Steps]` — the
 * indexer's own breadcrumbs, which mean nothing outside the retriever.
 *
 * So: the recorded fix, because that is what actually worked, or the article
 * text when there is no recorded fix — cleaned either way — and the link
 * last, introduced rather than dumped. The two are alternatives, not both:
 * together they say the same thing twice, at length.
 *
 * Never sent as-is. The composer is left for the agent to edit, which is
 * also how FR-KB-55 tells an accepted draft from an edited one.
 */
function replyText(s: TicketSuggestion): string {
  const nl = String.fromCharCode(10);
  const parts: string[] = [];
  const fix = s.fixes[0]?.fix?.trim();
  const body = articleForReply(s.body?.trim() ? s.body : cleanChunkText(s.snippet));

  if (fix) parts.push(fix);
  else if (body) parts.push(body);

  if (s.sourceUrl) parts.push(`Full instructions: ${s.sourceUrl}`);
  return parts.join(nl + nl);
}

/**
 * An article as a requester should read it: the steps as a numbered list and
 * how to tell it worked, without the Symptom and Applies-to headings, which
 * describe their own problem back to them. An Information article is its
 * answer and anything more, without the Question. Plain text, since it goes
 * into an email. An article that is not in sections is used as it is.
 */
export function articleForReply(text: string): string {
  const nl = String.fromCharCode(10);
  const p = splitArticle(text);
  if (p.format === 'information') {
    const said = [p.answer.trim(), p.more.trim()].filter(Boolean);
    return said.length ? said.join(nl + nl) : text.trim();
  }
  if (p.steps.length === 0) return text.trim();
  const out = [p.steps.map((x, i) => `${i + 1}. ${x}`).join(nl)];
  if (p.check.trim()) out.push(`To check it worked: ${p.check.trim()}`);
  if (p.escalation.trim()) out.push(`If that did not work: ${p.escalation.trim()}`);
  return out.join(nl + nl);
}
